//changed due to package upgrade
import React, { useState, useEffect } from 'react';
import { useAuthUser } from '@projectK/hooks/AuthHooks';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import PropTypes from 'prop-types';
import { alpha, Box, Button, Typography } from '@mui/material';
import AppGridContainer from '@projectK/components/AppGridContainer';
import Grid from '@mui/material/Grid';
import IntlMessages from '@projectK/helpers/IntlMessages';
import { useInfoViewActionsContext } from '@projectK/context/InfoViewContextProvider';
import jwtAxios from '@projectK/services/auth/JWT';
import { Fonts } from '@projectK/constants/AppEnums';
import AppTextField from '@projectK/components/AppTextField';
import AppInfoView from '@projectK/components/AppInfoView';
import CompanyTheme from '@projectK/constants/CompanyTheme';

const validationSchema = yup.object({
  parent1_mobile: yup
    .string()
    .matches(
      /^(\+\d{1,2}\s?)?(\()?\d{3}(\))?[-.\s]?\d{3}[-.\s]?\d{4}$/,
      'Please enter a valid phone number.',
    )
    .required('Please enter parent mobile number.'),
  parent2_mobile: yup
    .string()
    .matches(
      /^(\+\d{1,2}\s?)?(\()?\d{3}(\))?[-.\s]?\d{3}[-.\s]?\d{4}$/,
      'Please enter a valid phone number.',
    )
    .required('Please enter parent mobile number.'),
  parent1_name: yup
    .string()
    .matches(
      /^[A-Za-z0-9.'@,-]+(?:\s[A-Za-z0-9.'@,-]+)*$/,
      'Please enter valid name.',
    )
    .required('Please enter parent name.'),
  parent2_name: yup
    .string()
    .matches(
      /^[A-Za-z0-9.'@,-]+(?:\s[A-Za-z0-9.'@,-]+)*$/,
      'Please enter valid  name.',
    )
    .required('Please enter parent name.'),
  address: yup
    .string()
    .max(255, 'Address must be at most 255 characters.')
    .required('Please enter address.'),
  zip_code: yup
    .string()
    .matches(/^\d{5,6}$/, 'Zip code is not valid.')
    .required('Please enter zipcode.'),
});

const ContactInfo = ({ fetchData, onClose }) => {
  const companyTheme = CompanyTheme();
  const infoViewActionsContext = useInfoViewActionsContext();
  const { user } = useAuthUser();
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);
  const fetchSettings = async () => {
    try {
      const response = await jwtAxios.get(`/users/${user.id}`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };
  return (
    <>
      <Box>
        <Formik
          enableReinitialize={true}
          validateOnBlur={true}
          initialValues={{
            parent1_name: settings?.parent1_name || '',
            parent1_mobile: settings?.parent1_mobile || '',
            parent2_name: settings?.parent2_name || '',
            parent2_mobile: settings?.parent2_mobile || '',
            address: settings?.address || '',
            zip_code: settings?.zip_code + '' || '',
          }}
          validationSchema={validationSchema}
          onSubmit={(values, { setSubmitting }) => {
            setSubmitting(true);
            jwtAxios
              .put(`/users/${user.id}`, values)
              .then((response) => {
                setSubmitting(false);
                fetchSettings();
                infoViewActionsContext.showMessage(
                  'Contact Information Updated Successfully',
                );
              })
              .catch((error) => {
                setSubmitting(false);
                infoViewActionsContext.fetchError(error.response.data.error);
              });
          }}
        >
          {({ values, setFieldValue, handleChange, resetForm }) => {
            return (
              <Form noValidate autoComplete='off'>
                <Typography
                  component='h3'
                  sx={{
                    fontSize: 16,
                    fontWeight: Fonts.BOLD,
                    color: companyTheme
                      ? companyTheme.primary.main
                      : 'text.primary',
                  }}
                >
                  <IntlMessages id='common.contactInfo' />
                </Typography>
                <Grid size={{ xs: 12, md: 6 }}>
                  <AppGridContainer sx={{ mt: 2 }}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <AppTextField
                        name='parent1_name'
                        value={values.parent1_name}
                        variant='outlined'
                        sx={{
                          width: '100%',
                        }}
                        label='Parent1 Name'
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <AppTextField
                        name='parent1_mobile'
                        value={values.parent1_mobile}
                        variant='outlined'
                        sx={{
                          width: '100%',
                        }}
                        label='Parent1 Mobile Number '
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <AppTextField
                        name='parent2_name'
                        value={values.parent2_name}
                        variant='outlined'
                        sx={{
                          width: '100%',
                        }}
                        label='Parent2 Name'
                      />
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                      <AppTextField
                        name='parent2_mobile'
                        value={values.parent2_mobile}
                        variant='outlined'
                        sx={{
                          width: '100%',
                        }}
                        label='Parent2 Mobile Number '
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <AppTextField
                        name='address'
                        value={values.address}
                        variant='outlined'
                        sx={{
                          width: '100%',
                        }}
                        label='Address'
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <AppTextField
                        name='zip_code'
                        value={values.zip_code}
                        variant='outlined'
                        sx={{
                          width: '100%',
                        }}
                        label='Zip Code'
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 12 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Button
                          sx={{
                            position: 'relative',
                            minWidth: 100,
                            backgroundColor: companyTheme
                              ? companyTheme.primary.main
                              : '#2196f3',
                            '&:hover': {
                              backgroundColor: companyTheme
                                ? alpha(companyTheme.primary.main, 0.8) // Adjust the alpha value as needed
                                : alpha('#2196f3', 0.8),
                            },
                          }}
                          variant='contained'
                          type='submit'
                        >
                          Save
                        </Button>

                        <Button
                          sx={{
                            position: 'relative',
                            minWidth: 100,
                            ml: 2.5,
                            borderColor: companyTheme
                              ? companyTheme.primary.main
                              : '#2196f3',
                            '&:hover': {
                              borderColor: companyTheme
                                ? alpha(companyTheme.primary.main, 0.8)
                                : alpha('#2196f3', 0.8),
                            },
                            color: companyTheme
                              ? companyTheme.primary.main
                              : '#2196f3',
                          }}
                          variant='outlined'
                          onClick={(e) => {
                            e.preventDefault();
                            // Reset the form back to its initial state
                            resetForm();
                          }}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </Grid>
                  </AppGridContainer>
                </Grid>
              </Form>
            );
          }}
        </Formik>
      </Box>
      <AppInfoView />
    </>
  );
};

export default ContactInfo;

ContactInfo.propTypes = {
  fetchData: PropTypes.func,
  onClose: PropTypes.func,
};
